/**
 * Requêtes du verdict de dossier et des exigences — chunk 9.AC.1.
 *
 * Tout arrive **calculé du serveur** : statut, compteurs, « en attente de »,
 * prochaine action, retards. Ce module ne recompose rien. Décision PO explicite,
 * et propriété qui compte : le jour où la règle « une chose m'incombe → c'est à
 * moi de jouer » changera, elle changera à un seul endroit.
 */

import type { DbPage, ProjectCompleteness, ProjectRequirement, Schemas } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

/** GET /v1/projects/:id/completeness — lecture pure, aucune écriture serveur. */
export function useProjectCompleteness(projectId: string | undefined) {
  const qc = useQueryClient();
  const queryKey = ["projects", projectId, "completeness"] as const;
  return useQuery({
    queryKey,
    queryFn: async () => {
      const previous = qc.getQueryData<ProjectCompleteness>(queryKey);
      const verdict = await api.get<ProjectCompleteness>(`/v1/projects/${projectId}/completeness`);
      // Refresh the final transition too: once satisfied, the interval stops.
      if (previous?.waiting_on.includes("client") || verdict.waiting_on.includes("client")) {
        void qc.invalidateQueries({
          predicate: (query) => {
            const [resource, scope, identity] = query.queryKey;
            if (resource === "missing-information" && scope === "by-project") {
              return (identity as { projectId?: string } | undefined)?.projectId === projectId;
            }
            if (resource === "quote-requests" && scope === "readiness") {
              const request = qc.getQueryData<{ project_id: string }>([
                "quote-requests",
                "detail",
                identity,
              ]);
              return request?.project_id === projectId;
            }
            return false;
          },
          refetchType: "active",
        });
      }
      return verdict;
    },
    enabled: !!projectId,
    refetchInterval: (query) =>
      !query.state.error && query.state.data?.waiting_on.includes("client") ? 15_000 : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: (query) => query.state.data?.waiting_on.includes("client") ?? false,
    staleTime: 0,
  });
}

/** GET /v1/project-requirements?project_id=… */
export function useProjectRequirements(projectId: string | undefined) {
  return useQuery({
    queryKey: ["project-requirements", "list", projectId],
    queryFn: () =>
      api.get<DbPage<ProjectRequirement>>(
        `/v1/project-requirements?project_id=${encodeURIComponent(projectId!)}&limit=100`,
      ),
    enabled: !!projectId,
  });
}

/**
 * Invalide tout ce qui dépend de l'état du dossier.
 *
 * Le verdict ET la liste des manquants : une exigence ajoutée matérialise un
 * manquant côté serveur dans la même transaction, donc afficher l'un sans
 * rafraîchir l'autre montrerait deux vérités sur le même écran.
 */
function useInvalidateDossier(projectId: string | undefined) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["projects", projectId, "completeness"] });
    void qc.invalidateQueries({ queryKey: ["project-requirements", "list", projectId] });
    void qc.invalidateQueries({ queryKey: ["missing-information"] });
    void qc.invalidateQueries({ queryKey: ["quote-requests", "readiness"] });
  };
}

export type CreateRequirementInput = Schemas.CreateProjectRequirementInput;

/** POST /v1/project-requirements */
export function useCreateProjectRequirement(projectId: string | undefined) {
  const invalider = useInvalidateDossier(projectId);
  return useMutation({
    mutationFn: (input: CreateRequirementInput) =>
      api.post<ProjectRequirement>("/v1/project-requirements", input),
    onSuccess: invalider,
  });
}

/** DELETE /v1/project-requirements/:id — retrait soft. */
export function useDeleteProjectRequirement(projectId: string | undefined) {
  const qc = useQueryClient();
  const invalider = useInvalidateDossier(projectId);
  return useMutation({
    mutationFn: (id: string) => api.del<{ id: string }>(`/v1/project-requirements/${id}`),
    onSuccess: async (_, id) => {
      const queryKey = ["project-requirements", "list", projectId];
      await qc.cancelQueries({ queryKey });
      qc.setQueryData<DbPage<ProjectRequirement>>(queryKey, (previous) =>
        previous ? { ...previous, items: previous.items.filter((r) => r.id !== id) } : previous,
      );
      invalider();
    },
  });
}

export type CreateManualMissingInput = {
  project_id: string;
  label_fr: string;
  label_en: string;
  audience?: "client" | "broker" | "admin" | "insurer";
  due_at?: string | null;
};

/**
 * POST /v1/missing-information — l'ajout manuel du conseiller.
 *
 * Le serveur crée l'exigence `manual` ET son item en une transaction : côté
 * client il n'y a donc qu'un seul geste, et un seul concept.
 */
export function useCreateManualMissing(projectId: string | undefined) {
  const invalider = useInvalidateDossier(projectId);
  return useMutation({
    mutationFn: (input: CreateManualMissingInput) =>
      api.post<{ id: string }>("/v1/missing-information", input),
    onSuccess: invalider,
  });
}

/** POST /v1/projects/:id/completeness/reconcile — « regarde à nouveau ». */
export function useReconcileCompleteness(projectId: string | undefined) {
  const invalider = useInvalidateDossier(projectId);
  return useMutation({
    mutationFn: () =>
      api.post<{ created: number; fulfilled: number; completeness: ProjectCompleteness }>(
        `/v1/projects/${projectId}/completeness/reconcile`,
        {},
      ),
    onSuccess: invalider,
  });
}
